// The designed empty / loading / error states for the Antenna hub + composer (never a blank
// surface). The 403 branch mirrors the server deny: a Content seat (no antenna:read) that reaches /antenna
// directly gets the "not available for your role" state rather than a raw error (defense in depth — the
// composer entry is already hidden for it).
import { Megaphone } from "lucide-react";
import { Button, EmptyState, Icon, Spinner } from "@cntrl/ui";
import { ApiError } from "../../app/hooks/apiError.js";
import styles from "./AnnouncementStates.module.scss";

/** Centered spinner while a query loads. */
export function AnnouncementLoading({ label = "Loading announcements…" }: { label?: string }) {
  return (
    <div className={styles.center} role="status" aria-live="polite">
      <Spinner size={22} label={label} />
      <p className={styles.centerText}>{label}</p>
    </div>
  );
}

/** The born-blank empty state — a fresh fan club has no announcements yet (tenancy contract).*/
export function AnnouncementsEmpty({ onNew, canManage }: { onNew: () => void; canManage: boolean }) {
  return (
    <EmptyState
      title="No announcements yet"
      description="Post a single, a show, or a note and your fans see it in their Antenna feed."
      action={
        canManage ? (
          <Button variant="accent" onClick={onNew}>
            <Icon icon={Megaphone} size={16} /> New announcement
          </Button>
        ) : undefined
      }
    />
  );
}

/** Insufficient-capability state (server 403). File-local — reached via {@link AnnouncementQueryError}. */
function AnnouncementForbidden() {
  return (
    <EmptyState
      title="Antenna isn't available for your role"
      description="Your role on this band doesn't include the fan club."
    />
  );
}

/** Generic load error with a retry, showing the server message verbatim. File-local.*/
function AnnouncementError({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <EmptyState
      title="Couldn't load your fan club"
      description={message}
      action={<Button onClick={onRetry}>Retry</Button>}
    />
  );
}

/**
 * Renders the right state for a failed announcements query: 403 → the forbidden state (the role mirror of the
 * server 403); anything else → the generic error with the server detail verbatim + a retry.
 */
export function AnnouncementQueryError({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  const status = error instanceof ApiError ? error.status : 0;
  if (status === 403) return <AnnouncementForbidden />;
  const message = error instanceof Error && error.message ? error.message : "Please try again.";
  return <AnnouncementError message={message} onRetry={onRetry} />;
}
